import { GameDocumentContext } from '../../../contexts/game-document';
import React, { useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import {
  CopyTimerAsync,
  DeleteTimerAsync,
  GetResourceValue,
  UpdateGameDocState
} from '../../../utils/game-document';

import { Button } from '@progress/kendo-react-buttons';
import { process, State } from '@progress/kendo-data-query';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { LinkCell } from '../../../components/grid';
import EditDeleteCloneCell from '../../../components/grid/edit-delete-clone-cell';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { NoRecords } from '../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { ColumnMenu } from '../../../components/columnMenu';
import { useSessionStorage } from 'usehooks-ts';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { useBreadcrumb } from '../../../contexts/breadcrumb';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const Timers = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-timers',
    initialDataState
  );
  const [showConfirmationDelete, setShowConfirmationDelete] = useState('');

  const navigate = useNavigate();

  const timerData = React.useMemo(() => {
    if (!state.gameDocument) return [];
    return (state.gameDocument.assets.timers ?? []).map((timer) => ({
      ...timer,
      title: GetResourceValue(state.gameDocument!, timer.titleResId ?? '')
    }));
  }, [state.gameDocument]);

  useEffect(() => {
    // Set the page timer.
    document.title = `Timers - ${state.gameDocument?.name}`;
  }, [state]);

  const LinkedTitleCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props} />
  );

  const DeleteCell = (props: GridCellProps) => (
    <EditDeleteCloneCell
      onEditClick={() => navigate(`${props.dataItem['id']}`)}
      onCloneClick={() => onCloneEntity(props.dataItem['id'])}
      onDeleteClick={() => setShowConfirmationDelete(props.dataItem['id'])}
      {...props}
    />
  );

  const onCloneEntity = (entityId: string) => {
    CopyTimerAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTimerAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        setShowConfirmationDelete('');
      }
    );
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/timers/*', text: 'Timers' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar title={'Timers'}>
        <Button onClick={() => navigate('new')} themeColor={'primary'}>
          Add timer
        </Button>
      </Toolbar>

      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search timers'}
          columnsToSearch={['title', 'description']}
          showCardMode={false}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />
        <ExcelExport
          data={timerData}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} timers.xlsx`}>
          <Grid
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            className={'cg-grid3'}
            data={process(timerData, dataState)}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'title'}
              title={'Title'}
              columnMenu={ColumnMenu}
              cells={{ data: LinkedTitleCell }}
            />
            <Column
              field={'description'}
              columnMenu={ColumnMenu}
              title={'Description'}
            />

            <Column cells={{ data: DeleteCell }} />
            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(showConfirmationDelete)
              : setShowConfirmationDelete('')
          }
          onClose={() => setShowConfirmationDelete('')}>
          Are you sure you want to remove the timer?
        </YesNoDialog>
      )}
    </>
  );
};

export default Timers;
